import { parseRRule, recomputeShapedRrule, resolveEffectiveRrule } from "@app/core";
import { getServices } from "../../platform.js";
import { splitSeriesAt } from "./split-series.js";
import type { SeriesGet, SeriesSet } from "./types.js";

export async function rescheduleSeries(
  set: SeriesSet,
  get: SeriesGet,
  sourceId: string,
  oldOccurrence: Date,
  newDueAt: Date,
  tz?: string | null,
  rruleOverride?: string,
): Promise<void> {
  const source = get().tasks.find((t) => t.id === sourceId);
  if (!source || !source.recurrence_id) return;
  const rule = get().rules[source.recurrence_id]
    ?? await getServices().recurrence.findById(source.recurrence_id);
  if (!rule) return;

  const occurrenceIso = oldOccurrence.toISOString();
  const overrides = get().overrides[rule.id] ?? [];
  // An explicit cadence from the edit dialog wins verbatim; otherwise keep the
  // effective rrule, re-shaping byDay/byMonthDay when the move changed the slot.
  const effRrule = resolveEffectiveRrule(rule, occurrenceIso, overrides);
  const newRrule = rruleOverride
    ?? (shapeChanged(effRrule, oldOccurrence, newDueAt)
      ? recomputeShapedRrule(effRrule, newDueAt)
      : effRrule);

  await splitSeriesAt(set, get, sourceId, occurrenceIso, {
    newDueAt: newDueAt.toISOString(),
    newRrule,
    ...(tz !== undefined ? { tz } : {}),
  });
}

function shapeChanged(rrule: string, oldOcc: Date, newOcc: Date): boolean {
  const input = parseRRule(rrule);
  if (input.byDay && input.byDay.length > 0 && oldOcc.getDay() !== newOcc.getDay()) return true;
  if (input.byMonthDay && oldOcc.getDate() !== newOcc.getDate()) return true;
  return false;
}
